// Editor de template com inserção de variáveis.
// - Variável SEM opções: clicar/arrastar insere o token (ex: {routine}).
// - Variável COM opções: clicar abre menu para escolher um VALOR real.
// - kpiTelas (async): habilita o botão "+ KPI do Construtor" — escolhe tela, coluna e
//   agregação (soma/média/...) e insere {kpi:slug|coluna|agg}, resolvido no servidor.
import React, { useRef, useState } from 'react';

const AGGS = [
    { v: 'soma', l: 'Soma' }, { v: 'media', l: 'Média' }, { v: 'contagem', l: 'Contagem (linhas)' },
    { v: 'min', l: 'Mínimo' }, { v: 'max', l: 'Máximo' }, { v: 'ultimo', l: 'Último valor' },
];

export default function TemplateEditor({ value, onChange, vars = [], rows = 4, placeholder = '', kpiTelas = null }) {
    const ref = useRef(null);
    const [menu, setMenu] = useState(null);     // menu de valores de uma variável
    const [kpi, setKpi] = useState(null);        // wizard KPI: { telas, slug, coluna, agg, carregando }

    function insere(txt) {
        const ta = ref.current;
        const v = value || '';
        if (!ta) { onChange(v + txt); return; }
        const ini = ta.selectionStart != null ? ta.selectionStart : v.length;
        const fim = ta.selectionEnd != null ? ta.selectionEnd : v.length;
        onChange(v.slice(0, ini) + txt + v.slice(fim));
        requestAnimationFrame(() => { try { ta.focus(); const p = ini + txt.length; ta.setSelectionRange(p, p); } catch (_) {} });
    }

    async function clicaChip(v) {
        if (!v.opcoes) { insere(v.token); return; }
        setMenu({ token: v.token, label: v.label, carregando: true, opcoes: [] });
        let ops = v.opcoes;
        if (typeof ops === 'function') { try { ops = await ops(); } catch (_) { ops = []; } }
        setMenu({ token: v.token, label: v.label, carregando: false, opcoes: (ops || []).filter(Boolean) });
    }

    async function abreKpi() {
        setKpi({ carregando: true, telas: [], slug: '', coluna: '', agg: 'soma' });
        let telas = [];
        try { telas = await kpiTelas(); } catch (_) { telas = []; }
        setKpi({ carregando: false, telas: telas || [], slug: '', coluna: '', agg: 'soma' });
    }
    function telaSel() { return (kpi && kpi.telas.find(t => t.slug === kpi.slug)) || null; }
    function inserirKpi() {
        if (!kpi.slug) return;
        const ag = kpi.agg || 'soma';
        const col = ag === 'contagem' ? '' : (kpi.coluna || '');
        insere(`{kpi:${kpi.slug}|${col}|${ag}}`);
        setKpi(null);
    }

    return (
        <div style={{ position: 'relative' }}>
            <textarea
                ref={ref} className="textarea mono" rows={rows} value={value || ''} placeholder={placeholder}
                onChange={e => onChange(e.target.value)}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const t = e.dataTransfer.getData('text/plain'); if (t) insere(t); }}
            />
            {(vars.length > 0 || kpiTelas) && (
                <div style={{ marginTop: 8 }}>
                    <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
                        Clique para inserir (variáveis com ▾ deixam escolher um valor) · ou arraste para o texto:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {vars.map(v => (
                            <button key={v.token} type="button" className="chip" draggable
                                onDragStart={e => e.dataTransfer.setData('text/plain', v.token)}
                                onClick={() => clicaChip(v)} title={v.desc || v.token}
                                style={{ cursor: v.opcoes ? 'pointer' : 'grab', userSelect: 'none' }}>
                                <span className="mono">{v.token}</span>
                                {v.label ? <span style={{ opacity: 0.7 }}> · {v.label}</span> : null}
                                {v.opcoes ? <span style={{ marginLeft: 4 }}>▾</span> : null}
                            </button>
                        ))}
                        {kpiTelas && (
                            <button type="button" className="chip" onClick={abreKpi}
                                title="Inserir um indicador (KPI) calculado a partir de uma tela do Construtor"
                                style={{ cursor: 'pointer', borderStyle: 'dashed' }}>
                                + KPI do Construtor
                            </button>
                        )}
                    </div>
                </div>
            )}

            {menu && (
                <div style={{ position: 'absolute', zIndex: 60, marginTop: 4, left: 0, right: 0, background: '#fff', border: '1px solid var(--line)', boxShadow: '0 8px 28px -12px rgba(0,0,0,.4)', maxHeight: 280, overflow: 'auto', padding: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span className="eyebrow" style={{ fontSize: 10 }}>Valor para <span className="mono">{menu.token}</span></span>
                        <button type="button" className="icon-btn" onClick={() => setMenu(null)} style={{ cursor: 'pointer' }}>×</button>
                    </div>
                    <button type="button" className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 4 }}
                        onClick={() => { insere(menu.token); setMenu(null); }}>
                        ↻ Manter <span className="mono" style={{ marginLeft: 4 }}>{menu.token}</span> (valor automático)
                    </button>
                    {menu.carregando && <div className="muted" style={{ fontSize: 12, padding: 6 }}>carregando opções…</div>}
                    {!menu.carregando && menu.opcoes.length === 0 && <div className="muted" style={{ fontSize: 12, padding: 6 }}>Sem valores cadastrados.</div>}
                    {!menu.carregando && menu.opcoes.map((o, i) => (
                        <button key={i} type="button" className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 2, fontWeight: 400 }}
                            onClick={() => { insere(String(o)); setMenu(null); }}>{String(o)}</button>
                    ))}
                </div>
            )}

            {kpi && (
                <div style={{ position: 'absolute', zIndex: 60, marginTop: 4, left: 0, right: 0, background: '#fff', border: '1px solid var(--line)', boxShadow: '0 8px 28px -12px rgba(0,0,0,.4)', padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span className="eyebrow" style={{ fontSize: 10 }}>Indicador (KPI) do Construtor</span>
                        <button type="button" className="icon-btn" onClick={() => setKpi(null)} style={{ cursor: 'pointer' }}>×</button>
                    </div>
                    {kpi.carregando ? <div className="muted" style={{ fontSize: 12 }}>carregando telas…</div> : (
                        <>
                            <div className="field"><label>Tela (Construtor)</label>
                                <select className="select" value={kpi.slug} onChange={e => setKpi({ ...kpi, slug: e.target.value, coluna: '' })}>
                                    <option value="">— escolha —</option>
                                    {kpi.telas.map(t => <option key={t.slug} value={t.slug}>{t.titulo || t.slug}</option>)}
                                </select>
                            </div>
                            <div className="cfg-row-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <div className="field"><label>Agregação</label>
                                    <select className="select" value={kpi.agg} onChange={e => setKpi({ ...kpi, agg: e.target.value })}>
                                        {AGGS.map(a => <option key={a.v} value={a.v}>{a.l}</option>)}
                                    </select>
                                </div>
                                <div className="field"><label>Coluna {kpi.agg === 'contagem' ? '(não usada)' : ''}</label>
                                    <select className="select" value={kpi.coluna} disabled={kpi.agg === 'contagem' || !kpi.slug} onChange={e => setKpi({ ...kpi, coluna: e.target.value })}>
                                        <option value="">— escolha —</option>
                                        {(telaSel()?.colunas || []).map(c => <option key={c.valor} value={c.valor}>{c.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>
                                Insere <span className="mono">{`{kpi:${kpi.slug || 'tela'}|${kpi.agg === 'contagem' ? '' : (kpi.coluna || 'coluna')}|${kpi.agg}}`}</span> — calculado ao falar/enviar.
                            </div>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button type="button" className="btn ghost sm" onClick={() => setKpi(null)}>Cancelar</button>
                                <button type="button" className="btn sm" onClick={inserirKpi} disabled={!kpi.slug || (kpi.agg !== 'contagem' && !kpi.coluna)}>Inserir KPI</button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
